import { Form } from "@inertiajs/react"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import InputError from "@/components/input-error"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { useOnline } from "@/hooks/use-online"
import { toMessages } from "@/lib/form-errors"
import { candidatePath } from "@/routes"
import type { CandidateDetail } from "@/types/candidates"

/**
 * OKS-18 — Notes editor (UX Spec §Notes section).
 *
 * **Inertia `<Form>` only — Crew rule.** No shadcn Form, no
 * react-hook-form, no zod. Plain shadcn `Textarea` + `Label` with a
 * `name="candidate[notes]"` attribute (Rails strong-params nested
 * under `:candidate` — same shape as OKS-16's add-candidate dialog).
 * Errors render from the render-prop `errors.notes` field with
 * `role="alert"`.
 *
 * **Wire-format gotcha (Phase 4 E1 root cause, project memory
 * `playwright_form_canary`):** the `name=` MUST be `candidate[notes]`,
 * not `notes`. Top-level `notes` makes Inertia POST `{notes: "..."}`
 * which trips `params.require(:candidate)` at the controller →
 * 400 → no flash → no toast → silent failure. Vitest mocks pass
 * either shape; only Playwright catches the drift end-to-end.
 *
 * Read-only mode (`!can_edit`): renders the notes as plain muted
 * text in a card; no form, no inputs.
 *
 * Last-edited stamp surfaces above the editor when populated.
 *
 * Cancel resets the textarea to the server value (clears local typing
 * state). Save button is disabled while content matches the server
 * value (no-op submit prevention).
 *
 * Locked testids:
 *   - `candidate-detail-notes-section` — Card wrapper
 *   - `candidate-detail-notes-form` — `<form>` element
 *   - `candidate-detail-notes-input` — Textarea
 *   - `candidate-detail-notes-submit` — Save button
 *   - `candidate-detail-notes-cancel` — Cancel button
 *   - `candidate-detail-notes-stamp` — Last-edited line (when populated)
 *   - `candidate-detail-notes-error` — Error region
 *   - `candidate-detail-notes-readonly` — Read-only display
 */
interface CandidateNotesEditorProps {
  candidate: Pick<
    CandidateDetail,
    "id" | "notes" | "notes_updated_by" | "notes_updated_at"
  >
  canEdit: boolean
}

export function CandidateNotesEditor({
  candidate,
  canEdit,
}: CandidateNotesEditorProps) {
  const { t } = useTranslation()
  const online = useOnline()
  const serverValue = candidate.notes ?? ""
  const [value, setValue] = useState(serverValue)
  const dirty = value !== serverValue

  const stamp = renderLastEdited(t, candidate)

  return (
    <Card data-testid="candidate-detail-notes-section">
      <CardHeader>
        <CardTitle>{t("candidates.detail.notes.section_title")}</CardTitle>
        {stamp && (
          <p
            data-testid="candidate-detail-notes-stamp"
            className="text-muted-foreground text-xs"
          >
            {stamp}
          </p>
        )}
      </CardHeader>
      <CardContent>
        {!canEdit && (
          <p
            data-testid="candidate-detail-notes-readonly"
            className="text-muted-foreground bg-muted/30 rounded-md border p-3 text-sm whitespace-pre-line"
          >
            {serverValue.trim() || t("candidates.detail.notes.empty_readonly")}
          </p>
        )}
        {canEdit && (
          <Form
            action={candidatePath(candidate.id)}
            method="patch"
            data-testid="candidate-detail-notes-form"
            options={{ preserveScroll: true }}
          >
            {({ errors, processing }) => (
              <div className="flex flex-col gap-3">
                <Label htmlFor="candidate-notes-input" className="sr-only">
                  {t("candidates.detail.notes.section_title")}
                </Label>
                <Textarea
                  id="candidate-notes-input"
                  name="candidate[notes]"
                  rows={5}
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  placeholder={t("candidates.detail.notes.placeholder")}
                  disabled={processing}
                  aria-describedby="candidate-notes-error"
                  data-testid="candidate-detail-notes-input"
                  className="resize-y"
                />
                <div
                  id="candidate-notes-error"
                  data-testid="candidate-detail-notes-error"
                >
                  {/*
                   * OKS-608 — normalize the render-prop error via the shared
                   * `toMessages` helper. Previously `errors.notes` was passed
                   * RAW; a single-message server error can arrive as a bare
                   * string (inertia_rails flattening), which `InputError`'s
                   * `messages.join(", ")` would choke on and silently swallow
                   * (the OKS-601 EE4 latent failure class). This is the live
                   * fix for notes — email was hardened in OKS-601.
                   */}
                  <InputError messages={toMessages(errors.notes)} />
                </div>
                {!serverValue.trim() && !dirty && (
                  <p className="text-muted-foreground text-xs">
                    {t("candidates.detail.notes.empty_editor_helper")}
                  </p>
                )}
                {dirty && (
                  <p
                    aria-live="polite"
                    className="text-muted-foreground text-xs"
                  >
                    {t("candidates.detail.notes.unsaved_indicator")}
                  </p>
                )}
                {!online && (
                  <p className="text-muted-foreground text-xs" role="status">
                    {t("candidates.detail.notes.offline_disabled")}
                  </p>
                )}
                <div className="flex justify-end gap-2">
                  <Button
                    type="button"
                    variant="ghost"
                    disabled={!dirty || processing}
                    onClick={() => setValue(serverValue)}
                    data-testid="candidate-detail-notes-cancel"
                  >
                    {t("candidates.detail.notes.cancel")}
                  </Button>
                  <Button
                    type="submit"
                    disabled={!dirty || processing || !online}
                    data-testid="candidate-detail-notes-submit"
                  >
                    {processing
                      ? t("candidates.detail.notes.saving")
                      : t("candidates.detail.notes.save")}
                  </Button>
                </div>
              </div>
            )}
          </Form>
        )}
      </CardContent>
    </Card>
  )
}

function renderLastEdited(
  t: ReturnType<typeof useTranslation>["t"],
  candidate: Pick<CandidateDetail, "notes_updated_by" | "notes_updated_at">,
): string | null {
  if (!candidate.notes_updated_by || !candidate.notes_updated_at) return null
  const when = new Date(candidate.notes_updated_at)
  return t("candidates.detail.notes.last_edited", {
    by: candidate.notes_updated_by.name,
    when: when.toLocaleString(),
  })
}
